import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { ChevronRight, Footprints, UploadCloud } from "lucide-react";

import { Button } from "@/components/ui/button";
import { adminFetch } from "@/hooks/use-admin-session";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type ActivityDraft = {
  id?: string;
  title: string;
  description: string;
  metaLabel: string;
  metaValue: string;
  durationMinutes: string;
  difficulty: "" | "easy" | "medium" | "hard";
  coverImageUrl: string;
  iconKey: "" | "walk" | "meditation" | "learning" | "nutrition" | "other";
  featured: boolean;
  active: boolean;
  isPremium: boolean;
  tagsText: string;
  ritualIntent: string;
  ritualMaterials: string[];
  ritualSteps: { text: string; imageUrl: string }[];
  ritualPoints: string;
  ritualProgramDays: string;
  ritualProductIds: string[];
  ritualScheduleTime: string;
  activityPoints: string;
  relationKind: "" | "meditasyon" | "nefes" | "diyet" | "rituel";
  relationItemId: string;
  price: string;
  store_product_id?: string | null;
  kategori_id?: string;
  title_en?: string;
  title_es?: string;
  description_en?: string;
  description_es?: string;
};

// Google Play / RevenueCat'te tanımlı 3 sabit tek seferlik içerik fiyat kademesi.
const PRICE_TIERS = [
  { price: 999, storeProductId: 'icerik_999', label: '₺999,00 (icerik_999)' },
  { price: 1299, storeProductId: 'icerik_1299', label: '₺1.299,00 (icerik_1299)' },
  { price: 1499, storeProductId: 'icerik_1499', label: '₺1.499,00 (icerik_1499)' },
] as const;

const DIFFICULTY_OPTIONS: Array<{ value: ActivityDraft["difficulty"]; label: string }> = [
  { value: "", label: "—" },
  { value: "easy", label: "Kolay" },
  { value: "medium", label: "Orta" },
  { value: "hard", label: "Zor" },
];

const ICON_OPTIONS: Array<{ value: ActivityDraft["iconKey"]; label: string }> = [
  { value: "", label: "—" },
  { value: "walk", label: "Yürüyüş" },
  { value: "meditation", label: "Meditasyon" },
  { value: "learning", label: "Okuma" },
  { value: "nutrition", label: "Beslenme" },
  { value: "other", label: "Diğer" },
];

export function NumerolojiActivityEditor({
  draft,
  setDraft,
  onClose,
  onSave,
  isSaving,
  isRituelMode,
  ritualProducts,
  relationOptions,
}: {
  draft: ActivityDraft;
  setDraft: (next: ActivityDraft) => void;
  onClose: () => void;
  onSave: (payload: any) => void;
  isSaving: boolean;
  isRituelMode?: boolean;
  ritualProducts?: Array<{
    id: string;
    name: string;
    sku: string;
    priceTry: number;
    active: boolean;
  }>;
  relationOptions?: {
    meditasyon: Array<{ id: string; title: string; active: boolean }>;
    nefes: Array<{ id: string; title: string; active: boolean }>;
    diyet: Array<{ id: string; title: string; active: boolean }>;
    rituel: Array<{ id: string; title: string; active: boolean }>;
  };
}) {
  const isEdit = Boolean(draft.id);
  const title = isEdit ? "Aktiviteyi Düzenle" : "Yeni Aktivite Ekle";
  const { data: rituelKatData } = useQuery({
    queryKey: ['kategoriler', 'rituel'],
    queryFn: () => adminFetch('/api/admin/kategoriler?tur=rituel').then(r => r.json()),
    enabled: isRituelMode === true,
  });
  const rituelKategoriler: any[] = rituelKatData?.items ?? [];
  const [ritualProductQuery, setRitualProductQuery] = useState("");
  const [activeI18nLang, setActiveI18nLang] = useState<'en' | 'es' | null>(null);
  const coverFileRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const stepFileInputRef = useRef<HTMLInputElement | null>(null);
  const stepUploadIndexRef = useRef<number>(-1);
  const [uploadingStepIndex, setUploadingStepIndex] = useState<number | null>(null);
  const [stepImageError, setStepImageError] = useState<string | null>(null);

  const filteredRitualProducts = useMemo(() => {
    const list = ritualProducts ?? [];
    const q = ritualProductQuery.trim().toLocaleLowerCase("tr-TR");
    if (!q) return list;

    return list.filter((product) => {
      const hay = `${product.name} ${product.sku}`.toLocaleLowerCase("tr-TR");
      return hay.includes(q);
    });
  }, [ritualProductQuery, ritualProducts]);

  const uploadCoverImage = async (file: File) => {
    setMediaError(null);

    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setMediaError("Kapak görseli için max 6MB dosya yükleyebilirsiniz.");
      return;
    }

    setIsUploadingCover(true);

    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Dosya okunamadı."));
        reader.onload = () => {
          const result = typeof reader.result === "string" ? reader.result : "";
          const comma = result.indexOf(",");
          if (comma === -1) return reject(new Error("Dosya okunamadı."));
          resolve(result.slice(comma + 1));
        };
        reader.readAsDataURL(file);
      });

      const res = await adminFetch("/api/admin/numeroloji/activities/image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contentType: file.type || "image/jpeg",
          dataBase64,
          activityId: draft.id,
          activityTitle: draft.title,
        }),
      });

      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Resim yüklenemedi.");
      }

      const url = typeof json?.url === "string" ? json.url : "";
      if (!url) throw new Error("Resim URL alınamadı.");

      setDraft({ ...draft, coverImageUrl: url });
    } catch (err) {
      setMediaError(err instanceof Error ? err.message : "Resim yüklenemedi.");
    } finally {
      setIsUploadingCover(false);
    }
  };

  const uploadStepImage = async (file: File, stepIndex: number) => {
    setStepImageError(null);
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setStepImageError("Görsel için max 6MB dosya yükleyebilirsiniz.");
      return;
    }
    setUploadingStepIndex(stepIndex);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Dosya okunamadı."));
        reader.onload = () => {
          const result = typeof reader.result === "string" ? reader.result : "";
          const comma = result.indexOf(",");
          if (comma === -1) return reject(new Error("Dosya okunamadı."));
          resolve(result.slice(comma + 1));
        };
        reader.readAsDataURL(file);
      });
      const res = await adminFetch("/api/admin/numeroloji/activities/image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contentType: file.type || "image/jpeg",
          dataBase64,
          activityId: draft.id,
          activityTitle: draft.title,
        }),
      });
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Resim yüklenemedi.");
      const url = typeof json?.url === "string" ? json.url : "";
      if (!url) throw new Error("Resim URL alınamadı.");
      const next = [...draft.ritualSteps];
      next[stepIndex] = { ...next[stepIndex], imageUrl: url };
      setDraft({ ...draft, ritualSteps: next });
    } catch (err) {
      setStepImageError(err instanceof Error ? err.message : "Resim yüklenemedi.");
    } finally {
      setUploadingStepIndex(null);
    }
  };

  const linkedItems = useMemo(() => {
    if (draft.relationKind === "meditasyon") return relationOptions?.meditasyon ?? [];
    if (draft.relationKind === "nefes") return relationOptions?.nefes ?? [];
    if (draft.relationKind === "diyet") return relationOptions?.diyet ?? [];
    if (draft.relationKind === "rituel") return relationOptions?.rituel ?? [];
    return [];
  }, [draft.relationKind, relationOptions]);

  const savePayload = () => {
    const duration = draft.durationMinutes.trim();

    onSave({
      id: draft.id,
      title: draft.title,
      description: draft.description,
      metaLabel: draft.metaLabel.trim() || undefined,
      metaValue: draft.metaValue.trim() || undefined,
      durationMinutes: duration ? Number(duration) : undefined,
      difficulty: draft.difficulty || undefined,
      coverImageUrl: draft.coverImageUrl.trim() || undefined,
      iconKey: draft.iconKey || undefined,
      featured: Boolean(draft.featured),
      active: draft.active,
      isPremium: draft.isPremium,
      price: parseInt(draft.price) || 0,
      store_product_id: draft.store_product_id || null,
      tags: draft.tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      relationKind: isRituelMode ? "rituel" : draft.relationKind || undefined,
      relationItemId: draft.relationItemId.trim() || undefined,
      ritualIntent: draft.ritualIntent,
      ritualMaterials: draft.ritualMaterials,
      ritualSteps: draft.ritualSteps,
      ritualPoints: draft.ritualPoints,
      ritualProgramDays: draft.ritualProgramDays,
      ritualProductIds: draft.ritualProductIds,
      ritualScheduleTime: draft.ritualScheduleTime,
      activityPoints: draft.activityPoints,
      kategori_id: draft.kategori_id || null,
    });
  };

  const baseInput =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50";

  const formDisabled = Boolean(isSaving || isUploadingCover || uploadingStepIndex !== null);

  return (
    <div className="rounded-3xl border border-slate-200 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/40 sm:p-6">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
          <span>İçerik Yönetimi</span>
          <ChevronRight className="h-4 w-4" />
          <span>Aktiviteler</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-slate-900 dark:text-slate-100">{title}</span>
        </div>

        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Aktivite planlarını etiketleyerek kullanıcıya özel öneriler oluşturabilirsiniz.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="rounded-full bg-white/80 dark:bg-slate-950/40"
              onClick={onClose}
              disabled={formDisabled}
            >
              Vazgeç
            </Button>
            <Button className="rounded-full" onClick={savePayload} disabled={formDisabled}>
              {isSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Genel Bilgiler</div>

            <div className="mt-5 grid gap-5">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Başlık</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Örn: 20 Dakikalık Yürüyüş"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Açıklama</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[180px] resize-none")}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Aktivitenin kısa açıklaması..."
                />
              </div>

              <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Çeviriler</span>
                  <div className="flex gap-1">
                    {(["en", "es"] as const).map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => setActiveI18nLang(activeI18nLang === lang ? null : lang)}
                        className={cn(
                          "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                          activeI18nLang === lang
                            ? "bg-blue-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700",
                        )}
                      >
                        {lang === "en" ? "🇬🇧 EN" : "🇪🇸 ES"}
                      </button>
                    ))}
                  </div>
                </div>
                {activeI18nLang && (
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Başlık ({activeI18nLang.toUpperCase()})
                      </Label>
                      <Input
                        className={cn(baseInput)}
                        value={activeI18nLang === "en" ? (draft.title_en ?? "") : (draft.title_es ?? "")}
                        onChange={(e) => setDraft({ ...draft, [`title_${activeI18nLang}`]: e.target.value })}
                        placeholder={activeI18nLang === "en" ? "English title..." : "Título en español..."}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Açıklama ({activeI18nLang.toUpperCase()})
                      </Label>
                      <Textarea
                        className={cn(baseInput, "min-h-[100px] resize-none")}
                        value={activeI18nLang === "en" ? (draft.description_en ?? "") : (draft.description_es ?? "")}
                        onChange={(e) => setDraft({ ...draft, [`description_${activeI18nLang}`]: e.target.value })}
                        placeholder={activeI18nLang === "en" ? "English description..." : "Descripción en español..."}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Kart Bilgisi</div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Meta Etiketi</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.metaLabel}
                  onChange={(e) => setDraft({ ...draft, metaLabel: e.target.value })}
                  placeholder="Süre / Zorluk / Hedef"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Meta Değeri</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.metaValue}
                  onChange={(e) => setDraft({ ...draft, metaValue: e.target.value })}
                  placeholder="15 Dakika / Orta"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Süre (dk)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={draft.durationMinutes}
                  onChange={(e) => setDraft({ ...draft, durationMinutes: e.target.value })}
                  placeholder="15"
                />
              </div>

              {!isRituelMode ? (
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tamamlanınca Puan</Label>
                  <Input
                    className={cn(baseInput)}
                    inputMode="numeric"
                    value={draft.activityPoints}
                    onChange={(e) => setDraft({ ...draft, activityPoints: e.target.value })}
                    placeholder="3"
                  />
                </div>
              ) : null}

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Zorluk</Label>
                <select
                  className={cn(baseInput, "h-12")}
                  value={draft.difficulty}
                  onChange={(e) => setDraft({ ...draft, difficulty: e.target.value as any })}
                >
                  {DIFFICULTY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">İkon</Label>
                <select
                  className={cn(baseInput, "h-12")}
                  value={draft.iconKey}
                  onChange={(e) => setDraft({ ...draft, iconKey: e.target.value as any })}
                >
                  {ICON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {!isRituelMode ? (
                <>
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Aktivite Türü</Label>
                    <select
                      className={cn(baseInput, "h-12")}
                      value={draft.relationKind}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          relationKind: e.target.value as ActivityDraft["relationKind"],
                          relationItemId: "",
                        })
                      }
                    >
                      <option value="">—</option>
                      <option value="meditasyon">Meditasyon</option>
                      <option value="nefes">Nefes</option>
                      <option value="diyet">Diyet</option>
                      <option value="rituel">Ritüel</option>
                    </select>
                  </div>

                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Bağlı İçerik</Label>
                    <select
                      className={cn(baseInput, "h-12")}
                      value={draft.relationItemId}
                      disabled={!draft.relationKind}
                      onChange={(e) => setDraft({ ...draft, relationItemId: e.target.value })}
                    >
                      <option value="">İçerik seçin</option>
                      {linkedItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title}{item.active ? "" : " (pasif)"}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : null}

              <div className="grid gap-2 sm:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Etiketler (virgülle)</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.tagsText}
                  onChange={(e) => setDraft({ ...draft, tagsText: e.target.value })}
                  placeholder="genel, karmikborc1"
                />
              </div>

              {isRituelMode && (
                <div className="grid gap-2 sm:col-span-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kategori</Label>
                  <select
                    key={rituelKategoriler.length}
                    className={cn(baseInput)}
                    value={draft.kategori_id ?? ''}
                    onChange={(e) => setDraft({ ...draft, kategori_id: e.target.value || undefined })}
                  >
                    <option value="">— Kategori seç —</option>
                    {rituelKategoriler.map((k: any) => (
                      <option key={k.id} value={k.id}>{k.isim}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </Card>

          {isRituelMode ? (
            <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
              <div className="text-base font-bold text-slate-900 dark:text-white">Ritüel Akışı</div>

              <div className="mt-5 grid gap-6">
                {/* Niyet */}
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Ritüel Niyeti</Label>
                  <Input
                    className={cn(baseInput)}
                    value={draft.ritualIntent}
                    onChange={(e) => setDraft({ ...draft, ritualIntent: e.target.value })}
                    placeholder="Bu ritüelde niyetim..."
                  />
                </div>

                {/* Malzemeler — dinamik liste */}
                <div className="grid gap-3">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Malzemeler
                    <span className="ml-2 text-xs font-normal text-slate-400">({draft.ritualMaterials.length} adet)</span>
                  </Label>
                  {draft.ritualMaterials.map((m, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="shrink-0 text-xs font-bold text-slate-400 w-5 text-right">{i + 1}.</span>
                      <Input
                        className={cn(baseInput, "flex-1")}
                        value={m}
                        onChange={(e) => {
                          const next = [...draft.ritualMaterials];
                          next[i] = e.target.value;
                          setDraft({ ...draft, ritualMaterials: next });
                        }}
                        placeholder={`Malzeme ${i + 1}`}
                        disabled={formDisabled}
                      />
                      <button
                        type="button"
                        className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-950/30"
                        onClick={() => setDraft({ ...draft, ritualMaterials: draft.ritualMaterials.filter((_, j) => j !== i) })}
                        disabled={formDisabled}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-fit rounded-full text-sm"
                    onClick={() => setDraft({ ...draft, ritualMaterials: [...draft.ritualMaterials, ""] })}
                    disabled={formDisabled}
                  >
                    + Malzeme Ekle
                  </Button>
                </div>

                {/* Adımlar — dinamik liste */}
                <div className="grid gap-4">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Adımlar
                    <span className="ml-2 text-xs font-normal text-slate-400">({draft.ritualSteps.length} adım)</span>
                  </Label>
                  {draft.ritualSteps.map((step, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50/50 p-3 dark:border-slate-800 dark:bg-slate-950/20">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{i + 1}. Adım</span>
                        <button
                          type="button"
                          className="rounded-full p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-950/30"
                          onClick={() => setDraft({ ...draft, ritualSteps: draft.ritualSteps.filter((_, j) => j !== i) })}
                          disabled={formDisabled}
                        >
                          ×
                        </button>
                      </div>
                      <Textarea
                        className={cn(baseInput, "resize-none min-h-[80px]")}
                        value={step.text}
                        onChange={(e) => {
                          const next = [...draft.ritualSteps];
                          next[i] = { ...next[i], text: e.target.value };
                          setDraft({ ...draft, ritualSteps: next });
                        }}
                        placeholder={`${i + 1}. adım açıklaması...`}
                        disabled={formDisabled}
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          className={cn(baseInput, "flex-1 text-xs")}
                          value={step.imageUrl}
                          onChange={(e) => {
                            const next = [...draft.ritualSteps];
                            next[i] = { ...next[i], imageUrl: e.target.value };
                            setDraft({ ...draft, ritualSteps: next });
                          }}
                          placeholder="Görsel URL (opsiyonel)"
                          disabled={formDisabled}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="shrink-0 rounded-full bg-white/80 gap-1.5 text-xs dark:bg-slate-950/40"
                          onClick={() => {
                            stepUploadIndexRef.current = i;
                            stepFileInputRef.current?.click();
                          }}
                          disabled={formDisabled}
                        >
                          <UploadCloud className="h-3 w-3" />
                          {uploadingStepIndex === i ? "Yükleniyor..." : "Yükle"}
                        </Button>
                        {step.imageUrl && (
                          <button
                            type="button"
                            className="shrink-0 text-xs text-slate-400 hover:text-rose-500"
                            onClick={() => {
                              const next = [...draft.ritualSteps];
                              next[i] = { ...next[i], imageUrl: "" };
                              setDraft({ ...draft, ritualSteps: next });
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      {step.imageUrl && (
                        <img src={step.imageUrl} alt="" className="mt-2 h-24 w-full rounded-xl object-cover" />
                      )}
                      {stepImageError && uploadingStepIndex === null && stepUploadIndexRef.current === i && (
                        <div className="mt-1 text-xs text-destructive">{stepImageError}</div>
                      )}
                    </div>
                  ))}
                  <input
                    ref={stepFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      void uploadStepImage(file, stepUploadIndexRef.current);
                      e.currentTarget.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-fit rounded-full text-sm"
                    onClick={() => setDraft({ ...draft, ritualSteps: [...draft.ritualSteps, { text: "", imageUrl: "" }] })}
                    disabled={formDisabled}
                  >
                    + Adım Ekle
                  </Button>
                  {stepImageError && (
                    <div className="text-xs text-destructive">{stepImageError}</div>
                  )}
                </div>

                <div className="grid gap-2 sm:grid-cols-3 sm:items-end sm:gap-4">
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tamamlanınca Puan</Label>
                    <Input
                      className={cn(baseInput)}
                      inputMode="numeric"
                      value={draft.ritualPoints}
                      onChange={(e) => setDraft({ ...draft, ritualPoints: e.target.value })}
                      placeholder="25"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Hedef Süre (Gün)</Label>
                    <Input
                      className={cn(baseInput)}
                      inputMode="numeric"
                      value={draft.ritualProgramDays}
                      onChange={(e) => setDraft({ ...draft, ritualProgramDays: e.target.value })}
                      placeholder="40"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Uygulama Saati</Label>
                    <Input
                      type="time"
                      className={cn(baseInput)}
                      value={draft.ritualScheduleTime}
                      onChange={(e) => setDraft({ ...draft, ritualScheduleTime: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Mağazadan Önerilen Ürünler</Label>
                  {ritualProducts?.length ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-950/30">
                      <div className="mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        Seçili: {draft.ritualProductIds.length} • Toplam: {ritualProducts.length}
                      </div>

                      <Input
                        className={cn(baseInput, "mb-3 h-10")}
                        value={ritualProductQuery}
                        onChange={(e) => setRitualProductQuery(e.target.value)}
                        placeholder="Ürün adı veya SKU ara..."
                      />

                      <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
                        {filteredRitualProducts.map((product) => {
                          const checked = draft.ritualProductIds.includes(product.id);
                          return (
                            <label
                              key={product.id}
                              className="flex cursor-pointer items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-800 dark:bg-slate-900/50"
                            >
                              <span className="min-w-0">
                                <span className="block truncate font-semibold text-slate-800 dark:text-slate-100">{product.name}</span>
                                <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400">SKU: {product.sku}</span>
                              </span>
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? Array.from(new Set([...(draft.ritualProductIds ?? []), product.id]))
                                    : (draft.ritualProductIds ?? []).filter((id) => id !== product.id);
                                  setDraft({ ...draft, ritualProductIds: next });
                                }}
                              />
                            </label>
                          );
                        })}
                      </div>

                      {!filteredRitualProducts.length ? (
                        <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
                          Arama kriterine uygun ürün bulunamadı.
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
                      Ürün bulunamadı. Önce ürünler bölümünden ürün ekleyin.
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ) : null}

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-center justify-between gap-3">
              <div className="text-base font-bold text-slate-900 dark:text-white">Medya</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">(FTP yükleme + URL)</div>
            </div>

            <div className="mt-5 grid gap-5">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kapak görsel URL</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.coverImageUrl}
                  onChange={(e) => setDraft({ ...draft, coverImageUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <input
                ref={coverFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  void uploadCoverImage(file);
                  e.currentTarget.value = "";
                }}
              />

              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-4 text-slate-600 dark:border-slate-800 dark:bg-slate-950/20 dark:text-slate-300">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full bg-white/80 gap-2 dark:bg-slate-950/40"
                  onClick={() => coverFileRef.current?.click()}
                  disabled={formDisabled}
                >
                  <UploadCloud className="h-4 w-4" />
                  {isUploadingCover ? "Kapak yükleniyor..." : "Kapak görseli yükle"}
                </Button>

                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Dosya FTP'ye yüklenir ve URL alanı otomatik doldurulur.
                </div>

                {mediaError ? <div className="mt-2 text-xs text-destructive">{mediaError}</div> : null}
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Durum</div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Aktif</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Pasif ise kullanıcılar görmez.</div>
              </div>

              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.active}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Öne Çıkan</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Hero kartında kullanılabilir.</div>
              </div>

              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.featured}
                  onChange={(e) => setDraft({ ...draft, featured: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Premium</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Sadece premium üyeler görebilir.</div>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.isPremium}
                  onChange={(e) => setDraft({ ...draft, isPremium: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-amber-500 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div className="mt-4 grid gap-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Ürün Kademesi</label>
              <select
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-4 focus:ring-blue-600/20 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50"
                value={draft.store_product_id ?? (parseInt(draft.price) ? "custom" : "free")}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "free") {
                    setDraft({ ...draft, price: "0", store_product_id: null });
                  } else if (v === "custom") {
                    setDraft({ ...draft, store_product_id: null });
                  } else {
                    const tier = PRICE_TIERS.find((t) => t.storeProductId === v);
                    if (tier) setDraft({ ...draft, price: String(tier.price), store_product_id: tier.storeProductId });
                  }
                }}
              >
                <option value="free">Ücretsiz</option>
                {PRICE_TIERS.map((t) => (
                  <option key={t.storeProductId} value={t.storeProductId}>{t.label}</option>
                ))}
                <option value="custom">Serbest / Elle gir</option>
              </select>
            </div>

            <div className="mt-4 grid gap-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Fiyat (₺) — 0 ise ücretsiz</label>
              <input
                type="number"
                min={0}
                value={draft.price}
                disabled={Boolean(draft.store_product_id)}
                onChange={(e) => setDraft({ ...draft, price: e.target.value.replace(/[^0-9]/g, "") })}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-4 focus:ring-blue-600/20 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50"
                placeholder="0"
              />
            </div>

            <div
              className={cn(
                "mt-4 inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
                draft.active
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                  : "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300",
              )}
            >
              {draft.active ? "Yayında" : "Taslak"}
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
              <Footprints className="h-4 w-4" />
              İpucu
            </div>

            <div className="mt-4 rounded-2xl border border-blue-600/15 bg-blue-600/5 p-4 text-sm text-slate-700 dark:border-blue-500/20 dark:text-slate-200">
              <div className="font-semibold">Etiketleme</div>
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                Kişiye özel öneri için “karmikborc1” gibi etiketler kullanın. Genel öneriler için “genel” etiketi
                ekleyin. Ritüeller için “rituel” etiketi mutlaka bulunmalı.
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
